import { Suspense, useState, useEffect, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import enUS from 'antd/locale/en_US';
import { getTheme } from './theme';
import { AppLayout } from './components/layout';
import { LoadingSpinner, ErrorBoundary } from './components/common';
import { AdminGuard, LoginPage } from './features/auth';

// Lazy load pages for code splitting
const DashboardPage = lazy(() => import('./features/dashboard/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const UsersPage = lazy(() => import('./features/users/pages/UsersPage').then(m => ({ default: m.UsersPage })));
const RevenuePage = lazy(() => import('./features/revenue/pages/RevenuePage').then(m => ({ default: m.RevenuePage })));
const PaymentFailuresPage = lazy(() => import('./features/payments/pages/PaymentFailuresPage').then(m => ({ default: m.PaymentFailuresPage })));
const DiscountsPage = lazy(() => import('./features/discounts/pages/DiscountsPage').then(m => ({ default: m.DiscountsPage })));
const ReferralsPage = lazy(() => import('./features/referrals/pages/ReferralsPage').then(m => ({ default: m.ReferralsPage })));
const SignalsPage = lazy(() => import('./features/signals/pages/SignalsPage').then(m => ({ default: m.SignalsPage })));
const PromotionReadinessPage = lazy(() => import('./features/signals/pages/PromotionReadinessPage').then(m => ({ default: m.PromotionReadinessPage })));
const ExpirationPolicyPage = lazy(() => import('./features/signals/pages/ExpirationPolicyPage').then(m => ({ default: m.ExpirationPolicyPage })));
const AiQualityGatesPage = lazy(() => import('./features/signals/pages/AiQualityGatesPage').then(m => ({ default: m.AiQualityGatesPage })));
const SettingsPage = lazy(() => import('./features/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ActivityLogsPage = lazy(() => import('./features/activityLogs/pages/ActivityLogsPage').then(m => ({ default: m.ActivityLogsPage })));
const BugReportsPage = lazy(() => import('./features/bugReports/pages/BugReportsPage').then(m => ({ default: m.BugReportsPage })));
const SubscriptionPlansPage = lazy(() => import('./features/subscriptionPlans/pages/SubscriptionPlansPage').then(m => ({ default: m.SubscriptionPlansPage })));
const AlertsPage = lazy(() => import('./features/alerts/pages/AlertsPage').then(m => ({ default: m.AlertsPage })));
const CohortRetentionPage = lazy(() => import('./features/analytics/cohort/pages/CohortRetentionPage').then(m => ({ default: m.CohortRetentionPage })));
const RevenueDeepDivePage = lazy(() => import('./features/analytics/revenueDeepDive/pages/RevenueDeepDivePage').then(m => ({ default: m.RevenueDeepDivePage })));
const SignalPerformancePage = lazy(() => import('./features/analytics/signalPerformance/pages/SignalPerformancePage').then(m => ({ default: m.SignalPerformancePage })));
const UserBehaviorPage = lazy(() => import('./features/analytics/userBehavior/pages/UserBehaviorPage').then(m => ({ default: m.UserBehaviorPage })));
const FunnelConversionPage = lazy(() => import('./features/analytics/funnel/pages/FunnelConversionPage').then(m => ({ default: m.FunnelConversionPage })));
const AIConfigPage = lazy(() => import('./features/aiConfig/pages/AIConfigPage').then(m => ({ default: m.AIConfigPage })));
const MarketingPerformancePage = lazy(() => import('./features/marketingPerformance/pages/MarketingPerformancePage').then(m => ({ default: m.MarketingPerformancePage })));

// Broker-Sync admin pages (flat routes under the main layout; nav via the
// main sider's "Brokers" group — see src/config/navigation.tsx).
const BrokersOverview = lazy(() => import('./features/brokerSync/pages/BrokersOverview').then(m => ({ default: m.BrokersOverview })));
const BrokersAuditLogPage = lazy(() => import('./features/brokerSync/pages/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const BrokersCohortsPage = lazy(() => import('./features/brokerSync/pages/CohortsPage').then(m => ({ default: m.CohortsPage })));
const BrokersProvidersPage = lazy(() => import('./features/brokerSync/pages/ProvidersPage').then(m => ({ default: m.ProvidersPage })));
const BrokersPlanFeatureMappingPage = lazy(() => import('./features/brokerSync/pages/PlanFeatureMappingPage').then(m => ({ default: m.PlanFeatureMappingPage })));
const BrokersWebhookOpsPage = lazy(() => import('./features/brokerSync/pages/WebhookOpsPage').then(m => ({ default: m.WebhookOpsPage })));
const BrokersUserOpsPage = lazy(() => import('./features/brokerSync/pages/UserOpsPage').then(m => ({ default: m.UserOpsPage })));
const BrokersAvailabilityPage = lazy(() => import('./features/brokerSync/pages/BrokerAvailabilityPage').then(m => ({ default: m.BrokerAvailabilityPage })));

/**
 * Main application component
 * Sets up routing, theming, and authentication
 */
function App() {
  // Dark mode state with localStorage persistence
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('admin-dark-mode');
    return saved === 'true';
  });

  // Persist dark mode preference
  useEffect(() => {
    localStorage.setItem('admin-dark-mode', String(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode((prev) => !prev);

  return (
    <ConfigProvider theme={getTheme(isDarkMode ? 'dark' : 'light')} locale={enUS}>
      <AntApp>
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes with layout */}
              <Route
                element={
                  <AdminGuard>
                    <AppLayout isDarkMode={isDarkMode} onToggleDarkMode={toggleDarkMode} />
                  </AdminGuard>
                }
              >
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/revenue" element={<RevenuePage />} />
                <Route path="/payments" element={<PaymentFailuresPage />} />
                <Route path="/discounts" element={<DiscountsPage />} />
                <Route path="/referrals" element={<ReferralsPage />} />
                <Route path="/signals" element={<SignalsPage />} />
                <Route path="/signals/promotion-readiness" element={<PromotionReadinessPage />} />
                <Route path="/signals/expiration-policy" element={<ExpirationPolicyPage />} />
                <Route path="/signals/ai-quality-gates" element={<AiQualityGatesPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/ai-config" element={<AIConfigPage />} />
                <Route path="/marketing-performance" element={<MarketingPerformancePage />} />
                <Route path="/activity-logs" element={<ActivityLogsPage />} />
                <Route path="/bug-reports" element={<BugReportsPage />} />
                <Route path="/subscription-plans" element={<SubscriptionPlansPage />} />
                <Route path="/subscription-plans/v2" element={<Navigate to="/subscription-plans" replace />} />
                <Route path="/analytics/cohort-retention" element={<CohortRetentionPage />} />
                <Route path="/analytics/revenue-deep-dive" element={<RevenueDeepDivePage />} />
                <Route path="/analytics/signal-performance" element={<SignalPerformancePage />} />
                <Route path="/analytics/user-behavior" element={<UserBehaviorPage />} />
                <Route path="/analytics/funnel" element={<FunnelConversionPage />} />

                {/* Broker-Sync admin — flat routes under the main layout.
                    Each page self-wraps RoleGate(required="admin"); nav is the
                    main sider's "Brokers" group (no second in-content sider). */}
                <Route path="/admin/brokers" element={<BrokersOverview />} />
                <Route path="/admin/brokers/cohorts" element={<BrokersCohortsPage />} />
                <Route path="/admin/brokers/providers" element={<BrokersProvidersPage />} />
                <Route path="/admin/brokers/plan-features" element={<BrokersPlanFeatureMappingPage />} />
                <Route path="/admin/brokers/webhook-ops" element={<BrokersWebhookOpsPage />} />
                <Route path="/admin/brokers/user-ops" element={<BrokersUserOpsPage />} />
                <Route path="/admin/brokers/broker-availability" element={<BrokersAvailabilityPage />} />
                <Route path="/admin/brokers/audit" element={<BrokersAuditLogPage />} />
              </Route>

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
